import { useEffect, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { Crosshair, TrendingUp,
  LayoutDashboard, Inbox, ListChecks, MailOpen, Mail, ClipboardCheck, Users, Building2, UserCog, Settings as SettingsIcon, Menu, LogOut, Coins, Landmark, LayoutGrid, History, Receipt, MapPin, UserPlus, PhoneOff, MessageSquareText, ShieldCheck } from 'lucide-react'
import { Shell, SidebarGroup, Topbar, Chip, Button, Tooltip, Banner } from '../ds/index'
import { Icon } from './Icon'
import { useSession, isAdmin } from '../lib/session'
import ViewAs, { ViewingBar } from './ViewAs'
import Bell from './Bell'
import { registerServiceWorker } from './PhoneAlerts'
import { post } from '../lib/api'
import { BrandMark } from './BrandMark'
import { Moments } from './Moments'

type Item = { to: string; label: string; icon: typeof Inbox; badge?: number }
type Group = { label: string; items: Item[] }

export default function AppShell() {
  const [open, setOpen] = useState(false)
  const { me, config, refresh } = useSession()
  const admin = isAdmin(me)
  // So a tapped alert can open the lead even before alerts are turned on here.
  useEffect(() => { registerServiceWorker() }, [])

  const nav: Group[] = [
    ...(admin ? [{ label: 'Overview', items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/flow', label: 'Lead flow', icon: TrendingUp },
      { to: '/leads', label: 'Lead board', icon: ListChecks },
      { to: '/categories', label: 'By category', icon: LayoutGrid },
      { to: '/activity', label: 'Activity', icon: History },
      { to: '/billing', label: 'Billing', icon: Receipt },
    ] }, { label: 'Clients', items: [
      { to: '/clients', label: 'Clients', icon: Building2 },
      { to: '/applications', label: 'Sign-ups', icon: UserPlus },
      { to: '/targets', label: 'Target businesses', icon: Crosshair },
    ] }] : []),
    ...(me?.user.role !== 'admin' ? [{ label: 'My work', items: [
      { to: '/my', label: 'My leads', icon: Inbox, badge: me?.open_leads || undefined },
      ...(me?.user.role === 'rep' ? [{ to: '/flow', label: 'Lead flow', icon: TrendingUp }] : []),
      ...(me?.user.role === 'rep' ? [{ to: '/my/clients', label: 'Clients', icon: Building2 }, { to: '/my/targets', label: 'Target businesses', icon: Crosshair }, { to: '/settings', label: 'Settings', icon: SettingsIcon }] : []),
    ] }] : []),
    ...(admin ? [
      { label: 'Intake', items: [
        { to: '/mailbox', label: 'Mailbox', icon: Mail },
        { to: '/intake', label: 'Emails in', icon: MailOpen },
        { to: '/format-check', label: 'Format check', icon: ClipboardCheck },
      ] },
      { label: 'Setup', items: [
        { to: '/groups', label: 'Groups and reps', icon: Users },
        { to: '/zones', label: 'Zones', icon: MapPin },
        { to: '/dnc', label: 'Do Not Call', icon: PhoneOff },
        { to: '/replies', label: 'Saved replies', icon: MessageSquareText },
        { to: '/data-safety', label: 'Data safety', icon: ShieldCheck },
        { to: '/people', label: 'People', icon: UserCog },
        { to: '/settings', label: 'Settings', icon: SettingsIcon },
      ] },
    ] : []),
    ...(me?.user.role === 'spec' ? [{ label: 'SPEC only', items: [
      { to: '/economics', label: 'Economics', icon: Landmark },
      { to: '/costs', label: 'AI cost', icon: Coins },
    ] }] : []),
  ]

  const leave = async () => {
    await post('/api/auth/logout', {})
    await refresh()
  }

  const ai = config?.integrations?.ai

  return (
    <>
      {open && <div className="sidebar-backdrop only-mobile" onClick={() => setOpen(false)} role="presentation" />}
      <Shell
        sidebar={
          <nav className={open ? 'sidebar is-open' : 'sidebar'} aria-label="Main">
            <BrandMark fallbackName={me?.org.name || 'Leads'} />
            {nav.map((g) => (
              <div key={g.label}>
                <SidebarGroup label={g.label} />
                {g.items.map((it) => (
                  // Exact match when another item lives under this one (My leads at /my, Clients at /my/clients), so only one lights up.
                  <NavLink key={it.to} to={it.to} end={nav.some((x) => x.items.some((y) => y.to.startsWith(`${it.to}/`)))} className="nav-item" onClick={() => setOpen(false)}>
                    <Icon of={it.icon} />
                    <span>{it.label}</span>
                    {it.badge ? <span className="nav-badge">{it.badge}</span> : null}
                  </NavLink>
                ))}
              </div>
            ))}
            <div className="powered" style={{ marginTop: 'auto', padding: 'var(--s-6) var(--s-5)' }}>Powered by SPEC Systems</div>
          </nav>
        }
        topbar={
          <Topbar
            left={<>
              <button className="btn btn-ghost only-mobile" onClick={() => setOpen(true)} aria-label="Open menu"><Icon of={Menu} /></button>
              {config?.demoData && (
                <Tooltip label="Every rep, subscriber, group and post here is invented. The system is real; the records are not.">
                  <Chip tone="warn" dot>Demo records</Chip>
                </Tooltip>
              )}
              {admin && ai && !ai.live && (
                <span className="hide-phone">
                  <Tooltip label={ai.note || ''}>
                    <Chip tone={ai.adapter === 'none' ? 'neg' : 'warn'} dot>{ai.adapter === 'fake' ? 'AI simulated' : 'AI off'}</Chip>
                  </Tooltip>
                </span>
              )}
            </>}
            right={<>
              {me && <Bell />}
              {me && <ViewAs me={me} />}
              <Button variant="ghost" onClick={leave} aria-label="Sign out"><Icon of={LogOut} /></Button>
            </>}
          />
        }
      >
        {me?.viewing_as && <ViewingBar me={me} />}
        {admin && ai?.adapter === 'none' && (
          <div style={{ marginBottom: 'var(--s-6)' }}>
            <Banner tone="warn" title="No AI configured">New posts are stored and wait in the queue. Nothing is screened until an API key is set.</Banner>
          </div>
        )}
        <Outlet />
      </Shell>
      <Moments />
    </>
  )
}
